import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Form, Input, Button, Card, Typography, message, Space, Alert } from 'antd';
import { UserOutlined, LockOutlined, MailOutlined } from '@ant-design/icons';
import { api } from '../lib/api';

const { Text } = Typography;

export default function SignUpPage() {
  const [loading, setLoading] = useState(false);
  const [signupError, setSignupError] = useState('');
  const [contactEmail, setContactEmail] = useState('info@annea.sk');
  useEffect(() => {
    api.apps.config().then((config) => { if (config.contactEmail) setContactEmail(config.contactEmail); }).catch(() => {});
  }, []);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteEmail = searchParams.get('email') || '';
  const inviteToken = searchParams.get('invite') || '';
  const returnUrl = searchParams.get('return') || '/';

  const onFinish = async (values: { email: string; password: string; name: string }) => {
    try {
      setLoading(true);
      setSignupError('');
      await api.auth.signUp(values.email, values.password, values.name);
      if (inviteToken) {
        try {
          await api.invitations.acceptPublic(inviteToken);
          message.success('Account created and invitation accepted.');
          navigate('/login?return=/apps');
          return;
        } catch (error: any) {
          message.error(error?.message || 'Account created, but invitation acceptance failed.');
        }
      }
      message.success('Account created. Please check your email to verify.');
      navigate(returnUrl);
    } catch (error: any) {
      setSignupError(error.message || 'We could not create your account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const primaryColor = '#f46524';
  const cardStyle = {
    width: 420,
    maxWidth: '100%',
    borderRadius: 12,
    border: `1px solid ${primaryColor}`,
    boxShadow: '0 12px 35px rgba(244, 101, 36, 0.25)',
    background: '#fffdfa',
    overflow: 'hidden',
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        background: '#f5f5f5',
      }}
    >
      <Card
        title="Create Account"
        style={cardStyle}
        headStyle={{
          color: primaryColor,
          fontWeight: 600,
          textAlign: 'center',
          background: '#fffdfa',
          borderTopLeftRadius: 12,
          borderTopRightRadius: 12,
        }}
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div style={{ textAlign: 'center' }}>
            <Text type="secondary">Join Annea today</Text>
          </div>

          {signupError && <Alert type="error" showIcon message="We could not create your account" description={
            <Space direction="vertical"><Text>{signupError}</Text><a href={`mailto:${contactEmail}?subject=APO%20registration%20review`}>Contact Annea for help</a></Space>
          } />}

          <Form
            name="signup"
            onFinish={onFinish}
            autoComplete="off"
            layout="vertical"
            requiredMark={false}
            initialValues={{ email: inviteEmail }}
          >
            <Form.Item
              name="name"
              label="Name"
              rules={[{ required: true, message: 'Required' }]}
            >
              <Input
                prefix={<UserOutlined />}
                placeholder="Full name"
              />
            </Form.Item>

            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: 'Required' },
                { type: 'email', message: 'Please enter a valid email' },
              ]}
            >
              <Input
                prefix={<MailOutlined />}
                placeholder="Email address"
                disabled={Boolean(inviteEmail)}
              />
            </Form.Item>

            <Form.Item
              name="password"
              label="Password"
              rules={[
                { required: true, message: 'Required' },
                { min: 6, message: 'Password must be at least 6 characters' },
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="Password (min 6 characters)"
              />
            </Form.Item>

            <Form.Item
              name="confirmPassword"
              label="Confirm Password"
              dependencies={['password']}
              rules={[
                { required: true, message: 'Required' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Passwords do not match'));
                  },
                }),
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="Confirm password"
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                block
                loading={loading}
              >
                Sign Up
              </Button>
            </Form.Item>
          </Form>

          <div style={{ textAlign: 'center' }}>
            <Text type="secondary">Already have an account? </Text>
            <Link to="/login">Sign in</Link>
          </div>
        </Space>
      </Card>
    </div>
  );
}
